import React from 'react';
import {
  Modal,
  TextInput,
  Textarea,
  Select,
  Button,
  Group,
  Stack,
  ActionIcon,
  Text,
  Table,
  Title,
  Divider,
  Alert,
  Badge,
  TagsInput
} from '@mantine/core';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { IconPlus, IconTrash, IconInfoCircle } from '@tabler/icons-react';
import RangeTemplateSelector from 'components/RangeTemplateSelector';

export interface Question {
  id: string;
  title: string;
  category?: string;
  skalaCategory?: string;
  description?: string;
  type: 'Choose One' | 'Range' | 'Free-text';
  moduleId: string;
  moduleName?: string;
  groupId?: string;
  groupName?: string;
  respondentType: string;
  sequence?: number;
  rangeItems?: { label: string; value: string }[];
}

const rangeItemSchema = z.object({
  label: z.string().min(1, 'Label is required'),
  value: z.string().min(1, 'Value is required')
});

const questionSchema = z.object({
  title: z.string().min(1, 'Question is required'),
  description: z.string().optional(),
  type: z.enum(['Choose One', 'Range', 'Free-text']),
  rangeItems: z.array(rangeItemSchema).optional(),
  category: z.array(z.string()).default([]),
  skalaCategory: z.string()
});

type FormData = z.infer<typeof questionSchema>;

interface CreateQuestionModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (_question: Question) => void;
  moduleId: string;
  moduleName: string;
  respondentTypes: string[];
  externalTypeIds?: string[];
  externalTypeNames?: string[];
}

const CreateQuestionModal: React.FC<CreateQuestionModalProps> = ({
  opened,
  onClose,
  onSubmit,
  moduleId,
  moduleName,
  respondentTypes,
  externalTypeNames = []
}) => {
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = useForm<FormData>({
    resolver: zodResolver(questionSchema),
    defaultValues: {
      type: 'Choose One',
      rangeItems: []
    }
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'rangeItems'
  });

  const questionType = watch('type');

  const handleFormSubmit = (data: FormData) => {
    const respondentType = respondentTypes[0];

    const newQuestion: Question = {
      id: Date.now().toString(),
      title: data.title,
      description: data.description,
      category: data.category.join(', '),
      skalaCategory: data.skalaCategory || '',
      type: data.type,
      moduleId: moduleId,
      respondentType: respondentType,
      rangeItems: data.type === 'Range' ? data.rangeItems : undefined
    };

    onSubmit(newQuestion);
    reset();
    onClose();
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  const handleRangeTemplateSelect = (templateItems: any[]) => {
    replace(
      templateItems.map((item) => ({
        label: item.label,
        value: item.value
      }))
    );
  };

  React.useEffect(() => {
    if (!opened) {
      reset();
    }
  }, [opened, reset]);

  return (
    <Modal opened={opened} onClose={handleCancel} withCloseButton={false} size='lg' centered>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack gap='md' p={16}>
          <Title order={3}>Create New Question</Title>

          <Alert icon={<IconInfoCircle size={16} />} title='Question Information' color='blue'>
            When you create the question, it will not created in master question.<br></br>Creating
            question for <b>{respondentTypes.join(', ')}</b> assessment in module{' '}
            <b>{moduleName}</b>
            {respondentTypes.includes('External') && externalTypeNames.length > 0 && (
              <>
                <br />
                <br />
                <Text>External assessment types:</Text>
                <Group mt={5}>
                  {externalTypeNames.map((typeName, idx) => (
                    <Badge key={idx} color='blue' variant='light'>
                      {typeName}
                    </Badge>
                  ))}
                </Group>
              </>
            )}
          </Alert>

          <Controller
            name='title'
            control={control}
            render={({ field }) => (
              <TextInput
                label='Question'
                placeholder='How many tasks or projects do you typically complete in a week?'
                error={errors.title?.message}
                required
                {...field}
              />
            )}
          />

          <Controller
            name='category'
            control={control}
            render={({ field }) => (
              <TagsInput
                label='Question Categories (optional)'
                placeholder='Type and press Enter to add categories'
                data={[]}
                error={errors.category?.message}
                value={field.value}
                onChange={field.onChange}
                clearable
                maxTags={10}
              />
            )}
          />

          <Controller
            name='skalaCategory'
            control={control}
            render={({ field }) => (
              <TextInput
                label='Skala Category (optional)'
                placeholder='e.g., 1-5, 1-10'
                error={errors.title?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='description'
            control={control}
            render={({ field }) => (
              <Textarea
                label='Question Description (optional)'
                placeholder='Add additional context or instructions for the question'
                autosize
                minRows={2}
                maxRows={4}
                {...field}
              />
            )}
          />

          <Controller
            name='type'
            control={control}
            render={({ field }) => (
              <Select
                label='Type of Question'
                placeholder='Select question type'
                data={[
                  { value: 'Choose One', label: 'Choose One' },
                  { value: 'Range', label: 'Range' },
                  { value: 'Free-text', label: 'Free-text' }
                ]}
                error={errors.type?.message}
                required
                {...field}
              />
            )}
          />

          {questionType === 'Range' && (
            <>
              <Text fw={500} size='sm'>
                Range Items
              </Text>

              <Table striped withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Label</Table.Th>
                    <Table.Th>Value</Table.Th>
                    <Table.Th style={{ width: 40 }}></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {fields.map((field, index) => (
                    <Table.Tr key={field.id}>
                      <Table.Td>
                        <Controller
                          name={`rangeItems.${index}.label`}
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              placeholder='e.g., Poor'
                              error={errors.rangeItems?.[index]?.label?.message}
                              {...field}
                            />
                          )}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Controller
                          name={`rangeItems.${index}.value`}
                          control={control}
                          render={({ field }) => (
                            <TextInput
                              placeholder='e.g., 1'
                              error={errors.rangeItems?.[index]?.value?.message}
                              {...field}
                            />
                          )}
                        />
                      </Table.Td>
                      <Table.Td>
                        <ActionIcon color='red' onClick={() => remove(index)}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  {fields.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={3} style={{ textAlign: 'center' }}>
                        <Text c='dimmed' size='sm'>
                          No range items added
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>

              <Button
                variant='outline'
                leftSection={<IconPlus size={16} />}
                onClick={() => append({ label: '', value: '' })}
                color='#7B2183'
              >
                Add Range Item
              </Button>
              <Divider label='Or use a template' labelPosition='center' />
              <Group justify='center'>
                <RangeTemplateSelector onSelect={handleRangeTemplateSelect} />
              </Group>
            </>
          )}

          <Group justify='flex-end' mt='md'>
            <Button variant='outline' onClick={handleCancel} color='#7B2183'>
              Cancel
            </Button>
            <Button type='submit' color='#7B2183'>
              Add Question
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default CreateQuestionModal;
